// Schedule › Team manager › Hours worked in Blueprint (timesheet rules §4, §5): the team's pay period (HoursPage), a person's
// Timesheet on it (TimesheetPage, opened from a name, the board's tech menu, or the quick edit's
// Timesheet ›), and the quick edit over either. The reads: the periods (payPeriods.list for those who
// lock; a manager, who can't read that, gets the periods time.day gives), time.team, time.history,
// payroll.lockCheck, and each person's time.day for To fix. The days with something to fix read the
// board's own visits (dispatch.board), so To fix and the quick edit offer the booked end. Prepare and
// lock runs payroll.prepareAndLock with the start dates confirmed. With no pay period yet (QA-757),
// Hours and the Timesheet run on payroll's weeks (payroll.weekStartsOn, from time.team), with one quiet
// line about payroll and no lock: time tracking never waits on payroll. Minutes only, never pay.
import { useEffect, useMemo, useRef, useState } from 'react';
import { RpcError } from '@flowology/client';
import { addDays } from '@flowology/kernel';
import { HoursPage, TimesheetPage, type PageNotice, type ToFixRow } from '@flowology/ui-web/timesheet';
import { client } from '../api.ts';
import { errorText, useQuery } from '../books/ui.tsx';
import { todayIn } from '../books/model.ts';
import type { Board } from '../board-model.ts';
import { HOURS_NOT_YOURS, type NavProps } from '../Nav.tsx';
import {
  bookedFromBoard,
  hoursView,
  isStandIn,
  notOpenYet,
  periodOn,
  periodSegments,
  timeViewer,
  weekOf,
} from './hours-view-model.ts';
import { dayProblems } from './problems.ts';
import type { BookedVisit } from './quick-edit-model.ts';
import { QuickEditDialog, type QuickEditOpen } from './QuickEditDialog.tsx';
import { TeamTabs } from '../schedule/TeamTabs.tsx';
import type {
  LockCheck,
  PayPeriod,
  TeamPerson,
  TimeDay,
  TimeHistoryItem,
  TimeSheet,
} from './timesheet-types.ts';
import { timesheetView } from './timesheet-view-model.ts';

export interface HoursRouteProps {
  nav: NavProps;
  timeZone: string;
  /**
   * The board's link: this person's Timesheet, on the period a day is in; with no one (Books ›
   * Payroll's timesheets), Hours on that day's period.
   */
  open?: { staffId?: string; date?: string; at: number };
  /** The way back for an old link that isn't theirs. */
  onBoard?: () => void;
  /** Books › Payroll, once a period is locked; and its first run, before there's a period. */
  onPayroll?: () => void;
}

type ListedPeriod = Pick<PayPeriod, 'id' | 'startDate' | 'endDate' | 'payDate' | 'status'>;
const asPeriod = (p: ListedPeriod): PayPeriod => ({
  lockedAt: null,
  lockedBy: null,
  sentAt: null,
  sentBy: null,
  ...p,
});

export function HoursRoute({ nav, timeZone, open, onBoard, onPayroll }: HoursRouteProps) {
  const today = todayIn(timeZone);
  const held = nav.permissions ?? [];
  const locks = held.includes('payroll.prepare');
  const viewer = timeViewer(nav.roles, held);
  const [person, setPerson] = useState<string | null>(open?.staffId ?? null);
  const [onDate, setOnDate] = useState(open?.date ?? today);
  const [periodId, setPeriodId] = useState<string | null>(null);
  const [quick, setQuick] = useState<QuickEditOpen | null>(null);
  const [confirmed, setConfirmed] = useState<string[]>([]);
  // a save or a lock reads everything again
  const [round, setRound] = useState(0);
  useEffect(() => {
    if (!open) return;
    setPerson(open.staffId ?? null);
    setOnDate(open.date ?? todayIn(timeZone));
    setPeriodId(null);
  }, [open?.staffId, open?.date, open?.at, timeZone]);

  // ── the periods: listed for those who lock; a manager gets the ones time.day carries ──
  const listed = useQuery<{ periods: ListedPeriod[] }>('payPeriods.list', locks ? { limit: 8 } : null, [
    round,
  ]);
  const noneListed = locks && !!listed.data && listed.data.periods.length === 0;
  const recent = { from: addDays(today, -27), to: today };
  // the team (and payroll's week start) without a period: a manager's anchor, and anyone's weeks
  const anchorTeam = useQuery<{ people: TeamPerson[]; weekStartsOn: number }>(
    'time.team',
    !locks || noneListed ? recent : null,
  );
  const anchorId = person ?? anchorTeam.data?.people[0]?.staffId ?? null;
  const anchor = useQuery<TimeSheet>(
    'time.day',
    locks || !anchorId ? null : { staffId: anchorId, from: addDays(onDate, -27), to: onDate },
    [round],
  );
  // QA-774: past the newest period's end, the next one stands in until it's opened on Payroll, so this
  // week's hours show (time.team and time.day read any dates); Hours says it isn't open yet
  const periods: PayPeriod[] = useMemo(() => {
    const known = locks ? (listed.data?.periods ?? []).map(asPeriod) : (anchor.data?.periods ?? []);
    const next = notOpenYet(known, today);
    return next ? [...known, next] : known;
  }, [locks, listed.data, anchor.data, today]);
  const shownDate = onDate > today ? today : onDate;
  // QA-757: no pay period yet (payroll isn't set up), so the weeks payroll.weekStartsOn sets
  const noPayroll = locks
    ? noneListed && !!anchorTeam.data
    : !!anchorTeam.data && (!anchorId || (!!anchor.data && anchor.data.periods.length === 0));
  const week = noPayroll ? weekOf(shownDate, anchorTeam.data?.weekStartsOn ?? 0) : null;
  const period = week ?? periods.find((p) => p.id === periodId) ?? periodOn(periods, shownDate) ?? null;
  const range = period ? { from: period.startDate, to: period.endDate } : null;
  const stepWeek = (dir: -1 | 0 | 1) => {
    if (!week) return;
    const to = dir === 0 ? today : addDays(week.startDate, 7 * dir);
    setOnDate(to > today ? today : to);
  };
  const weekStep = week ? { isLatest: week.endDate >= today, onStep: stepWeek } : undefined;
  const setUp = noPayroll
    ? { noPayroll: { canSetUp: locks && !!onPayroll } }
    : period && isStandIn(period)
      ? { notOpen: { canOpen: locks && !!onPayroll } }
      : {};

  // ── the period's reads ──
  const team = useQuery<{ people: TeamPerson[] }>('time.team', range, [round]);
  // only a pay period locks: a week before payroll is set up has none
  const lock = useQuery<LockCheck>(
    'payroll.lockCheck',
    locks && period && !isStandIn(period) ? { periodId: period.id } : null,
    [round],
  );
  const history = useQuery<{ items: TimeHistoryItem[] }>(
    'time.history',
    // the period's every change (QA-815), as many as time.history gives
    range ? { ...range, ...(person ? { staffId: person } : {}), limit: 200 } : null,
    [round],
  );
  const sheet = useQuery<TimeSheet>('time.day', range && person ? { staffId: person, ...range } : null, [
    round,
  ]);
  // To fix on Hours: the days of each person with something to fix
  const flagged = (team.data?.people ?? []).filter((p) => p.leftOpen + p.flagged > 0).map((p) => p.staffId);
  const [days, setDays] = useState<{ key: string; by: Record<string, TimeDay[]> } | null>(null);
  const daysKey = range ? `${range.from}:${range.to}:${flagged.join(',')}:${round}` : '';
  useEffect(() => {
    if (!range || person || !team.current) return;
    let live = true;
    void Promise.all(
      flagged.map((staffId) =>
        client.call<TimeSheet>('time.day', { staffId, ...range }).then((s) => [staffId, s.days] as const),
      ),
    )
      .then((rows) => live && setDays({ key: daysKey, by: Object.fromEntries(rows) }))
      .catch(() => live && setDays(null));
    return () => {
      live = false;
    };
  }, [daysKey, person, team.current]);

  // the board's visits (dispatch.board, the board's own read) on the days with something to fix, by
  // person and day: one read a day serves everyone on it. A refusal leaves that day without them.
  const [booked, setBooked] = useState<Record<string, Record<string, BookedVisit[]>>>({});
  const boardDays = useRef(new Set<string>());
  const loadBooked = (dates: readonly string[]) => {
    for (const date of new Set(dates)) {
      if (boardDays.current.has(date)) continue;
      boardDays.current.add(date);
      void client
        .call<Board>('dispatch.board', { date })
        .then((b) =>
          setBooked((was) => {
            const next = { ...was };
            for (const t of b.technicians)
              next[t.id] = { ...next[t.id], [date]: bookedFromBoard(b, t.id, date, timeZone) };
            return next;
          }),
        )
        .catch(() => boardDays.current.delete(date));
    }
  };
  const problemDates = (list: readonly TimeDay[]) =>
    list.filter((d) => !d.locked && dayProblems(d, today).length).map((d) => d.date);
  useEffect(() => {
    if (days) loadBooked(Object.values(days.by).flatMap(problemDates));
  }, [days]);
  useEffect(() => {
    if (sheet.data) loadBooked(problemDates(sheet.data.days));
  }, [sheet.data]);

  const names = new Map((team.data?.people ?? anchorTeam.data?.people ?? []).map((p) => [p.staffId, p.name]));
  const segments = periodSegments(periods, today);
  const lockData = locks ? (lock.data ?? null) : null;
  const shown: PayPeriod | null =
    period && lockData
      ? {
          ...period,
          lockedAt: lockData.lockedAt ?? period.lockedAt,
          lockedBy: lockData.lockedBy ?? period.lockedBy,
        }
      : period;
  const failed =
    listed.error ?? anchorTeam.error ?? anchor.error ?? team.error ?? (person ? sheet.error : null);
  const forbidden = !held.includes('time.correct');
  const notice = (title: string): PageNotice | undefined =>
    forbidden
      ? {
          title,
          words: HOURS_NOT_YOURS,
          ...(onBoard ? { action: { label: 'The board ›', onPress: onBoard } } : {}),
        }
      : failed
        ? { title, words: failed, action: { label: 'Try again', onPress: () => setRound((n) => n + 1) } }
        : undefined;

  const openDay = (staffId: string, date: string, fix: string | null) => {
    loadBooked([date]);
    setQuick({ staffId, name: names.get(staffId) ?? 'Them', date, ...(fix ? { fix } : {}) });
  };
  // the day's booked visits once they're read (the strip's Booked row, the fixes' booked end)
  const quickBooked = quick ? booked[quick.staffId]?.[quick.date] : undefined;
  const quickOpen = useMemo(
    () => (quick ? { ...quick, ...(quickBooked ? { booked: quickBooked } : {}) } : null),
    [quick, quickBooked],
  );
  const dialog = quickOpen ? (
    <QuickEditDialog
      open={quickOpen}
      timeZone={timeZone}
      onClose={() => setQuick(null)}
      onSaved={() => setRound((n) => n + 1)}
      onTimesheet={(staffId, date) => {
        setQuick(null);
        setPerson(staffId);
        setOnDate(date);
        setPeriodId(null);
      }}
    />
  ) : null;

  if (person) {
    const name = names.get(person) ?? 'Timesheet';
    const view =
      shown && sheet.current && sheet.data
        ? timesheetView({
            person: { staffId: person, name, first: name.split(' ')[0] ?? name },
            sheet: sheet.data,
            period: shown,
            history: history.data?.items ?? [],
            viewer,
            locker: null,
            today,
            timeZone,
            ...(booked[person] ? { booked: booked[person] } : {}),
            ...setUp,
          })
        : null;
    return (
      <>
        <TeamTabs nav={nav} />
        <main aria-labelledby="tspage-title">
          <TimesheetPage
            view={view}
            notice={notice(name)}
            today={today}
            people={(team.data?.people ?? []).map((p) => ({ value: p.staffId, label: p.name }))}
            staffId={person}
            onPerson={(id) => setPerson(id)}
            periods={segments}
            periodId={period?.id ?? ''}
            onPeriod={setPeriodId}
            onOpenDay={(date, fix) => openDay(person, date, fix)}
            onHours={() => setPerson(null)}
            {...(weekStep ? { week: weekStep } : {})}
            {...(onPayroll ? { onSetUpPayroll: onPayroll } : {})}
          />
          {dialog}
        </main>
      </>
    );
  }

  const view =
    shown && team.current && team.data
      ? hoursView({
          period: shown,
          team: team.data.people,
          lock: lockData,
          history: history.data?.items ?? [],
          viewer,
          locker: null,
          sender: null,
          ...(days?.key === daysKey ? { days: days.by } : {}),
          booked,
          confirmed,
          today,
          timeZone,
          ...setUp,
        })
      : null;
  const lockNow = async () => {
    if (!period) return;
    try {
      const r = await client.call<LockCheck>('payroll.prepareAndLock', {
        periodId: period.id,
        ...(confirmed.length ? { withoutStatAndSickFor: confirmed } : {}),
      });
      setRound((n) => n + 1);
      if (!r.locked) throw new Error(r.blockers.map((b) => b.words).join(' ') || 'It didn’t lock.');
    } catch (e) {
      throw e instanceof RpcError ? new Error(errorText(e, 'It didn’t lock.'), { cause: e }) : e;
    }
  };
  return (
    <>
      <TeamTabs nav={nav} />
      <main aria-labelledby="hpage-title">
        <HoursPage
          view={view}
          notice={notice('Hours')}
          today={today}
          periods={segments}
          periodId={period?.id ?? ''}
          onPeriod={setPeriodId}
          onOpenDay={(r: ToFixRow, fix) => openDay(r.staffId, r.date, fix)}
          onPerson={(id) => setPerson(id)}
          confirmed={confirmed}
          onConfirm={(id, on) =>
            setConfirmed((c) => (on ? [...new Set([...c, id])] : c.filter((x) => x !== id)))
          }
          onLock={lockNow}
          {...(onPayroll ? { onPayroll, onSetUpPayroll: onPayroll } : {})}
          {...(weekStep ? { week: weekStep } : {})}
        />
        {dialog}
      </main>
    </>
  );
}
